// Return items template

/**
 * Creates the HTML for the returned items section of the receipt
 * @param {Object} order - The order containing the items
 * @param {Function} t - Translation function
 * @param {String} language - Current language
 * @param {Function} formatCurrency - Currency formatter function
 * @returns {String} - HTML content for the returned items section
 */
const createReturnItems = (order, t, language, formatCurrency) => {
  if (!order.items || !Array.isArray(order.items) || order.items.length === 0) {
    return '';
  }
  
  // This is a return receipt template by design
  
  // Create table rows for each item
  const itemRows = order.items.map((item, index) => {
    // Get item properties with fallbacks
    const name = language === 'ar' ? (item.name_ar || item.name) : (item.name || item.name_ar);
    const quantity = parseFloat(item.quantity) || 1;
    
    // Preserve the original unit price from the order
    let unitPrice = parseFloat(item.unit_price) || 0;
    
    // If unit_price is not available, try to calculate from price and quantity
    if (unitPrice === 0 && item.price) {
      unitPrice = parseFloat(item.price) / quantity;
    }
    
    // Make sure we're using the absolute value for display
    const displayUnitPrice = Math.abs(unitPrice);
    
    // For returns, ensure unit price is negative for calculations
    const calculationUnitPrice = unitPrice > 0 ? -unitPrice : unitPrice;
    
    // Calculate row total based on unit price and quantity
    let rowTotal = calculationUnitPrice * quantity;
    
    return `
      <tr>
        <td style="text-align: ${language === 'ar' ? 'right' : 'left'}; padding: 2px; font-family: monospace; font-size: 10px; border-bottom: 1px dotted #ccc;">
          ${name}
        </td>
        <td style="text-align: center; padding: 2px; font-family: monospace; font-size: 10px; border-bottom: 1px dotted #ccc;">
          ${quantity}
        </td>
        <td style="text-align: center; padding: 2px; font-family: monospace; font-size: 10px; border-bottom: 1px dotted #ccc;">
          ${displayUnitPrice.toFixed(2)}
        </td>
        <td style="text-align: ${language === 'ar' ? 'left' : 'right'}; padding: 2px; font-family: monospace; font-size: 10px; border-bottom: 1px dotted #ccc;">
          ${formatCurrency(rowTotal)}
        </td>
      </tr>
    `;
  }).join('');

  return `
    <!-- Return Items Section -->
    <div style="
      margin: 5px 0;
      font-family: monospace;
      font-size: 10px;
      direction: ${language === 'ar' ? 'rtl' : 'ltr'};
    ">
      <!-- Return notice -->
      <div class="return-notice" style="
        text-align: center;
        font-size: 10px;
        font-weight: bold;
        margin: 5px 0;
        border-top: 1px solid #000;
        border-bottom: 1px solid #000;
        padding: 3px 0;
      ">
        ${language === 'ar' ? t('cashier.receipt.returnedItems') : t('cashier.receipt.returnedItems')}
      </div>
      
      <!-- Items Table -->
      <table style="width: 100%; border-collapse: collapse; margin-top: 3px;">
        <thead>
          <tr>
            <th style="text-align: ${language === 'ar' ? 'right' : 'left'}; padding: 2px; font-family: monospace; font-size: 10px; border-bottom: 1px solid #000;">
              ${language === 'ar' ? t('cashier.receipt.item') : t('cashier.receipt.item')}
            </th>
            <th style="text-align: center; padding: 2px; font-family: monospace; font-size: 10px; border-bottom: 1px solid #000;">
              ${language === 'ar' ? t('cashier.receipt.qty') : t('cashier.receipt.qty')}
            </th>
            <th style="text-align: center; padding: 2px; font-family: monospace; font-size: 10px; border-bottom: 1px solid #000;">
              ${language === 'ar' ? t('cashier.receipt.price') : t('cashier.receipt.price')}
            </th>
            <th style="text-align: ${language === 'ar' ? 'left' : 'right'}; padding: 2px; font-family: monospace; font-size: 10px; border-bottom: 1px solid #000;">
              ${language === 'ar' ? t('cashier.receipt.total') : t('cashier.receipt.total')}
            </th>
          </tr>
        </thead>
        <tbody>
          ${itemRows}
        </tbody>
      </table>
    </div>
  `;
};

export default createReturnItems;
